"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import useMeasure from "react-use-measure";
import { twMerge } from "tailwind-merge";

import { orientedUrlMap } from "@/data/constants/oriented";
import { en } from "@/data/lang";
import { Lang } from "@/i18n-config";
import { getTranslations } from "@/utils/i18n/get-translations";

import { useSymbolInfo } from "../../services/symbol-info";
import { getNavigationList } from "../data/navigation-list";

type Props = {
  lang: Lang;
  symbol: string;
};

export const NavigationLinks = ({ lang, symbol }: Props) => {
  const { t } = getTranslations(lang);
  const pathname = usePathname();
  const { data: symbolInfo, isLoading, isError } = useSymbolInfo(symbol);
  const list = getNavigationList({
    lang,
    t,
    symbol,
    country: symbolInfo?.country ?? "US",
  });
  const [ref, { width }] = useMeasure();

  return isLoading || isError ? null : (
    <nav ref={ref} className="flex flex-1 flex-col px-5" data-hidden={!width}>
      <ul role="list" className="space-y-1.5">
        {list.map((item) => (
          <li key={item.key}>
            {item.key !== "overview" && !symbolInfo?.[item.key] ? (
              <button
                className="py-1.5 pl-5 text-base font-bold text-zinc-400"
                disabled
              >
                {item.title}
              </button>
            ) : (
              <Link
                href={`${item.pageLink}#analysis`}
                className={twMerge(
                  "group flex py-1.5 pl-5 text-base font-bold",
                  pathname.endsWith(item.pageLink.split("#")[0])
                    ? "border-l-4 border-sky-600 bg-gray-50 bg-sky-700/5 pl-4"
                    : "hover:bg-sky-700/5"
                )}
                aria-label={`go to ${
                  item.key === "overview"
                    ? "overview"
                    : orientedUrlMap[item.key]
                } page`}
              >
                <span
                  className={twMerge(
                    "mr-1.5 text-zinc-800",
                    pathname.endsWith(item.pageLink) && "text-sky-700"
                  )}
                >
                  {item.title}
                </span>
                {lang === "zh" && (
                  <span
                    className={twMerge(
                      "text-zinc-500",
                      pathname.endsWith(item.pageLink) && "text-sky-700"
                    )}
                  >
                    {en.analysis.shared.oriented[item.key]}
                  </span>
                )}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};
